import { useCallback, useEffect, useState } from 'react';
import * as api from '@company/services/chat/api';
import { useQuery } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import IConversationData, {
  IConversationList,
  MessageJob,
} from '@company/types/conversation.type';
import { AxiosError } from 'axios';
import { ThError, ThLoading } from '@components/elements';
import { isMobile } from 'react-device-detect';
import { Card, Col, Row } from 'react-bootstrap';
import { useChatContext } from '@company/state/chatContext';
import { useJobContext } from '@company/state/jobContext';
import { MINUTE } from '@constants/various';
import { clearCompanyIndicatorsCache } from '@company/hooks/useCompanyIndicators';
import IJobDetail from '@th-types/job.detail.type';
import Conversations from './Conversations';
import ConversationsMobile from './ConversationsMobile';

const mapJobDetailToMessageJob = (job: IJobDetail): MessageJob => ({
  id: job.id,
  localizedStart: job.localizedStart?.toString() || '',
  localizedEnd: job.localizedEnd?.toString() || '',
  eventName: job.eventName || '',
  brandRepresented: job.brandRepresented || '',
  companyName: job.company?.name || '',
  imageUrl: job.pictures?.[0] || '',
  jobTitle: job.friendlyJobTitle || job.title?.name || '',
});

interface Params {
  setMessageUnreadCount: (value: number) => void;
}

export default function Messaging({ setMessageUnreadCount }: Params) {
  const { job } = useJobContext();
  const [refetchInterval, setRefetchInterval] = useState(MINUTE * 15);
  const [selectedConversation, setSelectedConversation] =
    useState<IConversationData | null>(null);
  const [workerNameFilter, setWorkerNameFilter] = useState<string | null>(null);

  const fetchConversationsFn = useCallback(async () => {
    if (!job?.id) {
      return new Promise<IConversationList>((resolve) => {
        const emptyResponse: IConversationList = {
          results: [],
          total: 0,
          job: job
            ? mapJobDetailToMessageJob(job)
            : {
                id: 0,
                localizedStart: '',
                localizedEnd: '',
                eventName: '',
                brandRepresented: '',
                companyName: '',
                imageUrl: '',
                jobTitle: '',
              },
        };
        resolve(emptyResponse);
      });
    }

    return await api.fetchConversations(job?.id, workerNameFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, workerNameFilter]);

  const { isLoading, isFetching, error, data } = useQuery<
    IConversationList,
    AxiosError
  >({
    queryKey: [QueryKeys.MESSAGE_CONVERSATIONS, job?.id],
    queryFn: fetchConversationsFn,
    enabled: job?.id !== undefined,
    refetchInterval,
    refetchIntervalInBackground: false,
    retry: (failureCount) => failureCount < 3,
    retryDelay: MINUTE / 2,
  });

  const { setChatAssignmentId } = useChatContext();

  useEffect(() => {
    if (error) {
      setRefetchInterval(0);
    }
  }, [error]);

  const handleMessageUnreadCount = useCallback(async () => {
    /**
     * if data is undefined, setMessageUnreadCount to 0
     * if results is undefined or empty, setMessageUnreadCount to 0
     */
    if (!data?.results?.length) {
      setMessageUnreadCount(0);
      return;
    }

    const totalSum = data.results.reduce((accumulator, currentObject) => {
      return accumulator + (currentObject.unreadCount ?? 0); // if unreadCount is undefined, set it to 0
    }, 0);
    setMessageUnreadCount(totalSum);
  }, [data, setMessageUnreadCount]);

  useEffect(() => {
    handleMessageUnreadCount();
  }, [data, handleMessageUnreadCount]);

  if (error) {
    if (error.response?.status) {
      return <>No data found</>;
    }
    return <ThError message={error.message} />;
  }

  const handleReadConversation = async (
    conversation: IConversationData | null
  ) => {
    setSelectedConversation(conversation);

    if (conversation) {
      setChatAssignmentId(conversation.jobAssignmentId);

      const index = data?.results.findIndex(
        (conv) => conv.jobAssignmentId === conversation.jobAssignmentId
      );
      if (data && index) {
        data.results[index].unreadCount = 0;
      }
      handleMessageUnreadCount();

      // Only call read API if there are unread messages (commhub pattern)
      if (conversation.unreadCount > 0) {
        try {
          await api.readMessages(conversation.jobChatId);
        } catch (readError) {
          console.error('Failed to mark messages as read:', readError);
        }
      }
      clearCompanyIndicatorsCache();
    }
  };

  if (data && data.total === 0) {
    return (
      <>
        <Row>
          <Col>
            <b>Messaging</b>
          </Col>
        </Row>
        <Row className="pt-3">
          <Col>
            <Card className="p-3 rounded-4">
              Chat with applicants, workers you&apos;ve hired, and anyone you
              shared the job with. All started conversations will appear here.
            </Card>
          </Col>
        </Row>
      </>
    );
  }

  return (
    <div>
      {isFetching && <ThLoading />}
      {isMobile ? (
        <ConversationsMobile
          selectedConversation={selectedConversation}
          setSelectedConversation={handleReadConversation}
          setWorkerNameFilter={setWorkerNameFilter}
          conversations={data?.results}
          isLoading={isLoading}
        />
      ) : (
        <Conversations
          selectedConversation={selectedConversation}
          setSelectedConversation={handleReadConversation}
          setWorkerNameFilter={setWorkerNameFilter}
          conversations={data?.results}
          job={data?.job}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
